/**
 * SettingsPage - User Preferences + Creative Identity
 *
 * Central hub for all user settings including:
 * - Creative Identity (NEW — zones, skills, collaboration style)
 * - Account settings
 * - Content preferences (NSFW filtering)
 * - Notification preferences
 * - Privacy settings
 * - Appearance
 *
 * Place at: src/pages/SettingsPage.jsx
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Settings, Shield, Bell, Lock, User, Palette, Globe,
  ChevronRight, ArrowLeft, Save, Check, Sparkles,
  Package, Music, Utensils, BookOpen, Stethoscope, Cpu,
  TrendingUp, Zap, Users, Target, X, Plus
} from 'lucide-react';
import { cn } from '../utils/cn';
import { Button } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { NSFWSettingsPanel } from '../components/nsfw/NSFWFilter';
import { ZONES } from '../config/zones';
import { api } from '../api';
import CampusPicker from '../components/CampusPicker';
import { campusAPI } from '../api/campusAPI';

// ============================================================================
// ZONE ICON LOOKUP
// ============================================================================

const ZONE_ICONS = {
  build:     Package,
  beautiful: Palette,
  sound:     Music,
  taste:     Utensils,
  story:     BookOpen,
  heal:      Stethoscope,
  smart:     Cpu,
  sell:      TrendingUp,
};

// ============================================================================
// COLLABORATION STYLES
// ============================================================================

const COLLAB_STYLES = [
  { id: 'explorer',  label: 'Explorer',  desc: 'I love discovering and forking interesting projects', icon: '🔭' },
  { id: 'helper',    label: 'Helper',    desc: 'I enjoy responding to challenges and helping others', icon: '🤝' },
  { id: 'creator',   label: 'Creator',   desc: 'I mostly create original work and welcome forks',    icon: '✨' },
  { id: 'connector', label: 'Connector', desc: 'I love combining ideas across disciplines',           icon: '🔗' },
  { id: 'mentor',    label: 'Mentor',    desc: 'I want to guide and teach through feedback',          icon: '🎓' },
];

// ============================================================================
// SUGGESTED SKILLS per zone
// ============================================================================

const ZONE_SKILLS = {
  build:     ['woodworking', '3D printing', 'laser cutting', 'CNC', 'welding', 'ceramics', 'electronics', 'CAD'],
  beautiful: ['illustration', 'photography', 'graphic design', 'UI/UX', 'typography', 'color theory', 'Figma', 'Blender'],
  sound:     ['music production', 'mixing', 'guitar', 'piano', 'singing', 'sound design', 'Ableton', 'audio engineering'],
  taste:     ['baking', 'cooking', 'food photography', 'recipe development', 'fermentation', 'pastry', 'nutrition'],
  story:     ['creative writing', 'screenwriting', 'video editing', 'animation', 'journalism', 'poetry', 'filmmaking'],
  heal:      ['anatomy', 'nursing', 'mental health', 'physical therapy', 'public health', 'first aid', 'biology'],
  smart:     ['Python', 'JavaScript', 'React', 'machine learning', 'data science', 'robotics', 'API design', 'databases'],
  sell:      ['marketing', 'branding', 'business planning', 'sales', 'social media', 'pitching', 'analytics'],
};

// ============================================================================
// SETTINGS TABS
// ============================================================================

const SETTINGS_TABS = [
  { id: 'identity',      label: 'Creative Identity',    icon: Sparkles, description: 'Your zones, skills, and collaboration style' },
  { id: 'account',       label: 'Account',              icon: User,     description: 'Manage your profile and account details' },
  { id: 'content',       label: 'Content Preferences',  icon: Shield,   description: 'Control what content you see' },
  { id: 'notifications', label: 'Notifications',         icon: Bell,     description: 'Manage notification preferences' },
  { id: 'privacy',       label: 'Privacy',              icon: Lock,     description: 'Control your privacy settings' },
  { id: 'appearance',    label: 'Appearance',           icon: Palette,  description: 'Customize the look and feel' },
];

// ============================================================================
// CREATIVE IDENTITY TAB
// ============================================================================

const CreativeIdentityTab = ({ user, onSave }) => {
  const [activeZones,      setActiveZones]      = useState([]);
  const [collabStyle,      setCollabStyle]      = useState('');
  const [skills,           setSkills]           = useState([]);
  const [skillInput,       setSkillInput]       = useState('');
  const [currentProject,   setCurrentProject]   = useState('');
  const [openToCollab,     setOpenToCollab]     = useState(true);
  const [challengeInterest, setChallengeInterest] = useState(true);
  const [saved,            setSaved]            = useState(false);
  const [loading,          setLoading]          = useState(true);

  useEffect(() => {
    const loadIdentity = async () => {
      try {
        const res = await api.get('/user/creative-identity');
        if (res.data) {
          setActiveZones(res.data.zones || []);
          setCollabStyle(res.data.collab_style || '');
          setSkills(res.data.skills || []);
          setCurrentProject(res.data.current_project || '');
          setOpenToCollab(res.data.open_to_collab !== false);
          setChallengeInterest(res.data.challenge_interest !== false);
        }
      } catch {
        // No identity saved yet — fine
      }
      setLoading(false);
    };
    loadIdentity();
  }, []);

  const toggleZone = (zoneId) => {
    setActiveZones(prev =>
      prev.includes(zoneId) ? prev.filter(z => z !== zoneId) : [...prev, zoneId]
    );
  };

  const addSkill = (skill) => {
    const trimmed = skill.trim().toLowerCase();
    if (trimmed && !skills.includes(trimmed) && skills.length < 20) {
      setSkills(prev => [...prev, trimmed]);
    }
    setSkillInput('');
  };

  const removeSkill = (skill) => setSkills(prev => prev.filter(s => s !== skill));

  const handleSave = async () => {
    try {
      await api.put('/user/creative-identity', {
        zones:              activeZones,
        collab_style:       collabStyle,
        skills,
        current_project:    currentProject,
        open_to_collab:     openToCollab,
        challenge_interest: challengeInterest,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSave?.();
    } catch {
      // Silently fail — will add toast
    }
  };

  const suggestedSkills = activeZones
    .flatMap(z => ZONE_SKILLS[z] || [])
    .filter(s => !skills.includes(s.toLowerCase()));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Creative Identity</h2>
        <p className="text-sm text-zinc-400">
          Tell us about your creative interests. This helps surface relevant challenges, connections, and collaborators.
        </p>
      </div>

      {/* Zone Selection */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-3">Your Creative Zones</label>
        <p className="text-xs text-zinc-500 mb-3">Select the disciplines you're active in or curious about.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.values(ZONES).map(zone => {
            const Icon = ZONE_ICONS[zone.id] || Sparkles;
            const isActive = activeZones.includes(zone.id);
            return (
              <button
                key={zone.id}
                onClick={() => toggleZone(zone.id)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left transition-all duration-200',
                  isActive
                    ? 'border-transparent shadow-lg'
                    : 'border-zinc-700/50 hover:border-zinc-600 bg-zinc-800/30'
                )}
                style={isActive ? {
                  backgroundColor: zone.color + '18',
                  borderColor:     zone.color + '50',
                  boxShadow:       `0 0 20px ${zone.color}10`,
                } : {}}
              >
                <Icon
                  className="w-4 h-4 flex-shrink-0"
                  style={{ color: isActive ? zone.color : '#71717a' }}
                />
                <span className={cn('text-xs font-medium', isActive ? 'text-white' : 'text-zinc-400')}>
                  {zone.shortLabel}
                </span>
                {isActive && <Check className="w-3 h-3 ml-auto" style={{ color: zone.color }} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Collaboration Style */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-3">Collaboration Style</label>
        <p className="text-xs text-zinc-500 mb-3">How do you prefer to collaborate? Pick the one that fits best.</p>
        <div className="space-y-2">
          {COLLAB_STYLES.map(style => {
            const isActive = collabStyle === style.id;
            return (
              <button
                key={style.id}
                onClick={() => setCollabStyle(style.id)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all',
                  isActive
                    ? 'bg-purple-500/10 border-purple-500/40'
                    : 'bg-zinc-800/30 border-zinc-700/50 hover:border-zinc-600'
                )}
              >
                <span className="text-lg">{style.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-medium', isActive ? 'text-purple-300' : 'text-zinc-300')}>
                    {style.label}
                  </p>
                  <p className="text-xs text-zinc-500">{style.desc}</p>
                </div>
                {isActive && <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Skills */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-3">Skills You Can Offer</label>
        <p className="text-xs text-zinc-500 mb-3">
          What can you help others with? These appear when you respond to challenges.
        </p>

        <div className="flex flex-wrap gap-1.5 mb-3 min-h-[36px]">
          {skills.map(skill => (
            <span
              key={skill}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-500/15 text-purple-300 text-xs border border-purple-500/25"
            >
              {skill}
              <button onClick={() => removeSkill(skill)} className="hover:text-white transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {skills.length === 0 && (
            <span className="text-xs text-zinc-600 py-1">No skills added yet</span>
          )}
        </div>

        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={skillInput}
            onChange={e => setSkillInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(skillInput); } }}
            placeholder="Type a skill and press Enter..."
            className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50"
          />
          <Button size="sm" onClick={() => addSkill(skillInput)} disabled={!skillInput.trim()}>
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>

        {suggestedSkills.length > 0 && (
          <div>
            <p className="text-[11px] text-zinc-500 mb-1.5">Suggestions based on your zones:</p>
            <div className="flex flex-wrap gap-1">
              {suggestedSkills.slice(0, 12).map(skill => (
                <button
                  key={skill}
                  onClick={() => addSkill(skill)}
                  className="px-2 py-0.5 text-[11px] rounded-full bg-zinc-800/80 text-zinc-400 border border-zinc-700/50 hover:border-purple-500/30 hover:text-purple-300 transition-colors"
                >
                  + {skill}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Current Project */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">What are you working on?</label>
        <p className="text-xs text-zinc-500 mb-2">Visible on your profile — lets others know what you're up to.</p>
        <input
          type="text"
          value={currentProject}
          onChange={e => setCurrentProject(e.target.value)}
          placeholder='e.g., "Building a laser-cut chess set" or "Learning music production"'
          className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50"
          maxLength={120}
        />
        <p className="text-[11px] text-zinc-600 mt-1 text-right">{currentProject.length}/120</p>
      </div>

      {/* Quick toggles */}
      <div className="space-y-4">
        <NotificationToggle
          title="Open to Collaboration"
          description="Show a badge on your profile that you're available for projects"
          checked={openToCollab}
          onChange={setOpenToCollab}
        />
        <NotificationToggle
          title="Challenge Notifications"
          description="Get notified about new challenges in your active zones"
          checked={challengeInterest}
          onChange={setChallengeInterest}
        />
      </div>

      {/* Save */}
      <div className="pt-4 border-t border-zinc-800">
        <Button onClick={handleSave} icon={saved ? Check : Save}>
          {saved ? 'Saved!' : 'Save Creative Identity'}
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// SETTINGS PAGE COMPONENT
// ============================================================================

const SettingsPage = () => {
  const { user }                          = useAuth();
  const navigate                          = useNavigate();
  const [searchParams, setSearchParams]   = useSearchParams();
  const [activeTab, setActiveTab]         = useState(searchParams.get('tab') || 'identity');
  const [saved, setSaved]                 = useState(false);
  const [campusId, setCampusId] = useState(undefined);

  useEffect(() => {
    campusAPI.getMyCampus()
    .then(res => setCampusId(res.data?.campus?.id ?? undefined))
    .catch(() => {});
}, []);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleCampusSave = async (newCampusId) => {
    setCampusId(newCampusId);
    try {
      await campusAPI.setMyCampus(newCampusId);
    } catch (err) {
      console.error('Failed to update campus:', err);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Settings</h1>
            <p className="text-zinc-400 text-sm">Manage your account and preferences</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">

          {/* Sidebar */}
          <div className="w-full md:w-64 flex-shrink-0">
            <nav className="space-y-1">
              {SETTINGS_TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all',
                      isActive
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                    )}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{tab.label}</p>
                      <p className="text-xs text-zinc-500 truncate hidden md:block">{tab.description}</p>
                    </div>
                    <ChevronRight className={cn(
                      'w-4 h-4 flex-shrink-0 transition-transform md:hidden',
                      isActive && 'rotate-90'
                    )} />
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">

              {/* Creative Identity */}
              {activeTab === 'identity' && (
                <CreativeIdentityTab user={user} onSave={handleSave} />
              )}

              {/* Account Settings */}
              {activeTab === 'account' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-1">Account Settings</h2>
                    <p className="text-sm text-zinc-400">Manage your profile information</p>
                  </div>

                  <div className="space-y-4">
                    {/* Avatar */}
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white text-xl font-bold">
                        {user?.username?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <Button variant="secondary" size="sm">Change Photo</Button>
                        <p className="text-xs text-zinc-500 mt-1">JPG, PNG. Max 2MB</p>
                      </div>
                    </div>

                    {/* Username */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1.5">Username</label>
                      <input
                        type="text"
                        defaultValue={user?.username || ''}
                        className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1.5">Email</label>
                      <input
                        type="email"
                        defaultValue={user?.email || ''}
                        className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50"
                      />
                    </div>

                    {/* Bio */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-1.5">Bio</label>
                      <textarea
                        rows={3}
                        defaultValue={user?.bio || ''}
                        placeholder="Tell others about yourself..."
                        className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 resize-none"
                      />
                    </div>

                    {/* Campus */}
                    <CampusPicker
                      value={campusId}
                      onChange={handleCampusSave}
                    />
                  </div>

                  <div className="pt-4 border-t border-zinc-800">
                    <Button onClick={handleSave} icon={saved ? Check : Save}>
                      {saved ? 'Saved!' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Content Preferences */}
              {activeTab === 'content' && (
                <NSFWSettingsPanel />
              )}

              {/* Notifications */}
              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-1">Notification Preferences</h2>
                    <p className="text-sm text-zinc-400">Choose what notifications you receive</p>
                  </div>
                  <div className="space-y-4">
                    <NotificationToggle title="Fork Notifications"    description="When someone forks your project"                              defaultChecked={true} />
                    <NotificationToggle title="Challenge Responses"   description="When someone responds to your challenge"                      defaultChecked={true} />
                    <NotificationToggle title="Comments"              description="When someone comments on your project"                        defaultChecked={true} />
                    <NotificationToggle title="Zone Challenges"       description="New challenges in your active creative zones"                 defaultChecked={true} />
                    <NotificationToggle title="Cross-Zone Forks"      description="When your work inspires someone in a different discipline"    defaultChecked={true} />
                    <NotificationToggle title="New Followers"         description="When someone follows you"                                    defaultChecked={false} />
                    <NotificationToggle title="Weekly Digest"         description="Weekly summary of activity"                                  defaultChecked={true} />
                  </div>
                </div>
              )}

              {/* Privacy */}
              {activeTab === 'privacy' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-1">Privacy Settings</h2>
                    <p className="text-sm text-zinc-400">Control your privacy and visibility</p>
                  </div>
                  <div className="space-y-4">
                    <NotificationToggle title="Public Profile"   description="Allow others to see your profile"                       defaultChecked={true} />
                    <NotificationToggle title="Show Activity"    description="Display your recent activity on your profile"          defaultChecked={true} />
                    <NotificationToggle title="Allow Messages"   description="Let other users send you messages"                     defaultChecked={true} />
                    <NotificationToggle title="Show in Search"   description="Allow your profile to appear in search results"        defaultChecked={true} />
                  </div>
                  <div className="pt-4 border-t border-zinc-800">
                    <h3 className="text-sm font-semibold text-red-400 mb-3">Danger Zone</h3>
                    <Button variant="danger" size="sm">Delete Account</Button>
                    <p className="text-xs text-zinc-500 mt-2">
                      This action is irreversible. All your projects and data will be permanently deleted.
                    </p>
                  </div>
                </div>
              )}

              {/* Appearance */}
              {activeTab === 'appearance' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-1">Appearance</h2>
                    <p className="text-sm text-zinc-400">Customize how Enume looks</p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-3">Theme</label>
                      <div className="grid grid-cols-3 gap-3">
                        <ThemeOption name="Dark"   colors={['#18181b', '#27272a', '#a855f7']} selected={true} />
                        <ThemeOption name="Light"  colors={['#f4f4f5', '#e4e4e7', '#9333ea']} selected={false} disabled />
                        <ThemeOption name="System" colors={['#18181b', '#f4f4f5', '#a855f7']} selected={false} disabled />
                      </div>
                      <p className="text-xs text-zinc-500 mt-2">Light mode coming soon!</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-3">Map Node Density</label>
                      <input type="range" min="1" max="3" defaultValue="2" className="w-full accent-purple-500" />
                      <div className="flex justify-between text-xs text-zinc-500 mt-1">
                        <span>Sparse</span><span>Normal</span><span>Dense</span>
                      </div>
                    </div>
                    <NotificationToggle title="Reduce Motion"           description="Minimize animations for accessibility"          defaultChecked={false} />
                    <NotificationToggle title="Show Connection Labels"   description="Display labels on connection lines"            defaultChecked={false} />
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const NotificationToggle = ({ title, description, defaultChecked, checked: controlledChecked, onChange }) => {
  const [internalChecked, setInternalChecked] = useState(defaultChecked);
  const isControlled = controlledChecked !== undefined;
  const checked = isControlled ? controlledChecked : internalChecked;

  const handleClick = () => {
    if (isControlled) {
      onChange?.(!checked);
    } else {
      setInternalChecked(!checked);
    }
  };

  return (
    <label className="flex items-center justify-between cursor-pointer">
      <div>
        <p className="text-sm font-medium text-zinc-300">{title}</p>
        <p className="text-xs text-zinc-500">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={handleClick}
        className={cn(
          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
          checked ? 'bg-purple-600' : 'bg-zinc-700'
        )}
      >
        <span className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1'
        )} />
      </button>
    </label>
  );
};

const ThemeOption = ({ name, colors, selected, disabled }) => (
  <button
    disabled={disabled}
    className={cn(
      'relative p-3 rounded-xl border-2 transition-all text-left',
      selected ? 'border-purple-500 bg-purple-500/10' : 'border-zinc-700 hover:border-zinc-600',
      disabled && 'opacity-50 cursor-not-allowed'
    )}
  >
    <div className="flex gap-1 mb-2">
      {colors.map((color, idx) => (
        <div key={idx} className="w-4 h-4 rounded-full" style={{ backgroundColor: color }} />
      ))}
    </div>
    <p className="text-sm font-medium text-zinc-300">{name}</p>
    {selected && (
      <div className="absolute top-2 right-2">
        <Check className="w-4 h-4 text-purple-400" />
      </div>
    )}
  </button>
);

export default SettingsPage;